import isNil from "lodash/isNil";
import { OnSuccessData } from "PFApp/booking/components/booking_form/use_handle_submit/use_handle_submit";
import BookingDeleteButton from "PFApp/booking/components/delete_booking_modal/booking_delete_button";
import {
  DetailsPanelSource,
  DetailsPanelType,
  useDetailsPanelsActions
} from "PFApp/booking/components/details_panel";
import { useBookingActivityContext } from "PFApp/booking/parts/providers/booking_activity_context_provider";
import { Alert } from "PFComponents/alert";
import { LoadingDots } from "PFComponents/loading_dots";
import { utc } from "PFCore/helpers/date";
import { isProfileActive } from "PFCore/helpers/profile";
import { useBooking } from "PFCore/hooks/queries";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { useIsUnbiasedMode } from "PFCore/hooks/use_is_unbiased_mode";
import { useUnbiasedProfileName } from "PFCore/hooks/use_unbiased_profile_name";
import { Profile } from "PFCore/types";
import { Booking } from "PFTypes/booking";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import css from "../../detail_view.module.scss";
import { useBookingDetailSidePanelContext } from "../booking_detail_side_panel_context";
import { CloneButton } from "./clone_button";
import { EditButton } from "./edit_button";
import { ReassignButton } from "./reassign_button";

interface ButtonsProps {
  onDeleted?: (booking: Booking) => Promise<void>;
  onEdit: (data: OnSuccessData) => void;
  source?: DetailsPanelSource;
}

const Buttons = ({ onDeleted, onEdit, source }: ButtonsProps) => {
  const { closeDetailsPanel } = useDetailsPanelsActions();
  const { t } = useTranslation(["bookingModule", "translation"]);
  const { activity } = useBookingActivityContext();
  const { profile, siblingBookings, activeBookingIndex, bookingTemplate, isBookingTemplateFetching } =
    useBookingDetailSidePanelContext();

  const { getUnbiasedProfileName } = useUnbiasedProfileName();
  const isUnbiasedMode = useIsUnbiasedMode(activity?.reviewers);

  const currentSiblingBooking = !isNil(activeBookingIndex)
    ? siblingBookings?.[activeBookingIndex]
    : undefined;

  const { getCategory } = useBookingCategories();
  const isCategoryHidden = currentSiblingBooking ? !getCategory(currentSiblingBooking) : false;
  // buttons need to fetch full booking details to check source and readonly
  const { data: booking, isFetching: isBookingFetching } = useBooking(currentSiblingBooking?.id, {
    enabled: !!currentSiblingBooking?.id && !isCategoryHidden
  });

  const isSingleDayBookingTemplate = useMemo(
    () => bookingTemplate && utc(bookingTemplate.end_date).diff(bookingTemplate.start_date, "days") === 0,
    [bookingTemplate]
  );

  if (isBookingFetching) {
    return <LoadingDots centered circlesEnabled circleSize="xs" className={css.buttonsLoader} />;
  } else if (!booking || isCategoryHidden) {
    return null;
  }

  const { id, booking_template_id: bookingTemplateId, source: bookingSource, readonly } = booking || {};

  const cloneEnabled = isProfileActive(profile) && !booking?.booking_template_id;

  const handleDelete = (entity: Booking) => {
    closeDetailsPanel(DetailsPanelType.Booking, id);
    closeDetailsPanel(DetailsPanelType.Overbookings, entity.profile_id);
    return onDeleted ? onDeleted(entity) : Promise.resolve();
  };

  const handleClone = (data: OnSuccessData) => {
    closeDetailsPanel(DetailsPanelType.Booking, id);
    return onEdit(data);
  };

  const profileName = getUnbiasedProfileName(profile as Profile, isUnbiasedMode);

  return (
    <div className={css.footer}>
      {bookingSource === "ui" ? (
        !readonly && (
          <div className={css.buttons}>
            <EditButton
              bookingId={id}
              isRepeatedBooking={Number.isInteger(bookingTemplateId)}
              onSuccess={onEdit}
              source={source}
              disabled={Number.isInteger(bookingTemplateId) && isBookingTemplateFetching}
            />
            {cloneEnabled && <CloneButton bookingId={id} onSuccess={handleClone} source={source} />}
            <ReassignButton booking={booking} title={t("bookingModule:bookings.reassign.reassignBooking")} />
            <BookingDeleteButton
              bookingId={id}
              profileFullName={profileName}
              onDeleted={handleDelete}
              isSingleDayBookingTemplate={isSingleDayBookingTemplate}
              title={t("bookingModule:bookings.delete.deleteBooking")}
            />
          </div>
        )
      ) : (
        <Alert title={t("bookingModule:bookings.delete.externalSourceWarning")} hideIcon />
      )}
    </div>
  );
};

export default Buttons;
